import React, { useState, useEffect } from 'react';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import FormDialog from '../../../components/form-dialog/ui/FormDialog';
import MainSlider from '../components/ui/MainSlider';
import SlidingText from '../components/ui/SlidingText';
import PresentationSection from '../components/ui/PresentationSection';
import CatalogSection from '../components/ui/CatalogSection';
import ServicesSection from '../components/ui/ServicesSection';
import AboutSection from '../components/ui/AboutSection';
import TransparentPricingSection from '../components/ui/TransparentPricingSection';
import WhyChooseSection from '../components/ui/WhyChooseSection';
import BrandSection from '../components/ui/BrandSection';
import VideoSection from '../components/ui/VideoSection';
import FAQSection from '../components/ui/FAQSection';
import AppointmentSection from '../components/ui/AppointmentSection';
import BeforeAfterSection from '../components/ui/BeforeAfterSection';
import HoursSection from '../components/ui/HoursSection';
import { initializeTitleAnimation } from '../components/script/title-animation';
import { seoData } from '../../../../data/seoData';
import '../style/home-page.css';

const HomePage: React.FC = () => {
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        message?: string;
    }>({
        isOpen: false,
        type: 'success'
    });

    // Expose dialog function to window for use by appointment form handler
    useEffect(() => {
        const showContactDialog = (type: 'success' | 'error', message?: string) => {
            setDialogState({
                isOpen: true,
                type,
                message
            });
        };

        // Expose function to window
        (window as any).showContactDialog = showContactDialog;

        return () => {
            // Cleanup
            delete (window as any).showContactDialog;
        };
    }, []);

    const handleCloseDialog = () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
    };

    // 🛡️ CRITICAL: Mobile unlock mechanism - ensure page is scrollable immediately and stays unlocked
    useEffect(() => {
        const unlockPageForMobile = () => {
            // Remove any blocking classes
            document.body.classList.remove('page-loading');
            document.body.classList.remove('home-page--loading');
            
            // Only remove blocking styles if not in a modal (check for modal presence)
            const hasModal = document.querySelector('.catalog-preview-modal, .about-modal, .video-modal');
            if (!hasModal) {
                // Remove blocking styles only if no modal is open
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.height = '';
                document.body.style.top = '';
            }
            
            document.documentElement.style.overflow = '';
            document.documentElement.style.position = '';
            
            // Ensure home page is visible
            const homePage = document.querySelector('.home-page');
            if (homePage) {
                homePage.classList.remove('home-page--loading');
                (homePage as HTMLElement).style.overflow = '';
                (homePage as HTMLElement).style.height = '';
            }
        };

        // Run immediately
        unlockPageForMobile();
        
        // Run continuously every 50ms during initial load (first 10 seconds) - more aggressive
        let checkCount = 0;
        const maxChecks = 200; // 10 seconds at 50ms intervals
        const continuousUnlock = setInterval(() => {
            unlockPageForMobile();
            checkCount++;
            if (checkCount >= maxChecks) {
                clearInterval(continuousUnlock);
            }
        }, 50);
        
        // Also listen for catalog loading events
        const handleCatalogLoaded = () => {
            unlockPageForMobile();
        };
        document.addEventListener('catalogImagesLoaded', handleCatalogLoaded);
        
        // Run on touch events (mobile interaction)
        const handleTouch = () => {
            unlockPageForMobile();
        };
        document.addEventListener('touchstart', handleTouch, { passive: true });
        document.addEventListener('touchmove', handleTouch, { passive: true });
        
        // Run on scroll events (if user tries to scroll, ensure it works)
        const handleScroll = () => {
            unlockPageForMobile();
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            clearInterval(continuousUnlock);
            document.removeEventListener('catalogImagesLoaded', handleCatalogLoaded);
            document.removeEventListener('touchstart', handleTouch);
            document.removeEventListener('touchmove', handleTouch);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Initialize animations after page loads
    useEffect(() => {
            // Wait a bit for the page to be fully visible and layout to settle
            const timer = setTimeout(() => {
                // Refresh ScrollTrigger to recalculate positions
                if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                    (window as any).ScrollTrigger.refresh();
                }
                
                // Re-initialize title animations after ScrollTrigger refresh
                setTimeout(() => {
                    initializeTitleAnimation();
                }, 100);
            }, 300);

            return () => clearTimeout(timer);
    }, []);

    return (
        <>
            <SEO 
                title={seoData.home.title}
                description={seoData.home.description}
                keywords={seoData.home.keywords}
                canonical={seoData.home.canonical}
            />
            <div className="home-page">
                <Navbar />
                <MainSlider />
                <SlidingText />
                <PresentationSection />
                <CatalogSection />
                <ServicesSection />
                <TransparentPricingSection />
                <AboutSection />
                <WhyChooseSection />
                <BrandSection />
                <VideoSection />
                <BeforeAfterSection />
                <FAQSection />
                <SlidingText/>
                <HoursSection />
                <AppointmentSection />
                <Footer />
                <ScrollToTop />
                <FormDialog
                    isOpen={dialogState.isOpen}
                    type={dialogState.type}
                    message={dialogState.message}
                    onClose={handleCloseDialog}
                />
            </div>
        </>
    );
};

export default HomePage;

